import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../../contexts/AuthContext';
import { useInView, reveal } from './motion';
import { useCardTilt } from './useCardTilt';
import { useCapability } from './three/useCapability';
import { cardImage } from '../../lib/cardAssets';
import { useT, useLang } from '../../i18n/LanguageContext';

// Back-to-front dealing order: z1 lands first, z3 (the top card) lands last.
const fanLayout = [
  { id: 'E2', rot: -16, x: -150, y: 34, z: 1 },
  { id: 'D5', rot: -7, x: -78, y: 8, z: 2 },
  { id: 'A1', rot: 15, x: 90, y: 22, z: 2 },
  { id: 'C3', rot: 4, x: 4, y: 0, z: 3 },
];

// The focal moment: cards start stacked at the deck's origin (no rotation,
// slightly shrunk, as if held face-down together) and fan out to their
// resting spread — a literal "dealing the hand" gesture, not a generic
// rise-and-fade. Each card's own destination is read from CSS custom
// properties set inline, so one keyframe drives all four.
const deal = keyframes`
  from {
    transform: translate(0, 0) rotate(0deg) scale(0.86);
    opacity: 0;
  }
  to {
    transform: translate(var(--tx), var(--ty)) rotate(var(--trot)) scale(1);
    opacity: 1;
  }
`;

export default function Hero() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { ref, visible } = useInView({ once: true });
  const capability = useCapability();
  const t = useT();
  const { lang } = useLang();
  const fan = fanLayout.map((card) => ({ ...card, src: cardImage(card.id, lang) }));

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative', overflow: 'hidden', bgcolor: theme.home.paper,
        '&::before': {
          content: '""', position: 'absolute', inset: 0,
          backgroundImage: theme.home.grain, pointerEvents: 'none',
        },
      }}
    >
      {/* warm ambient glow */}
      <Box sx={{
        position: 'absolute', top: -180, right: -120, width: 520, height: 520, borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.home.coral}33, transparent 65%)`, filter: 'blur(8px)',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -200, left: -160, width: 480, height: 480, borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.home.cyan}26, transparent 68%)`,
      }} />

      <Box sx={{
        position: 'relative', maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 },
        pt: { xs: 12, md: 16 }, pb: { xs: 9, md: 13 },
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
        gap: { xs: 7, md: 4 }, alignItems: 'center',
      }}>
        {/* Left — copy */}
        <Box>
          <Typography variant="overline" sx={{ color: theme.home.coralText, display: 'inline-flex', alignItems: 'center', gap: 1, ...reveal(visible) }}>
            <Box component="span" sx={{ width: 28, height: 2, bgcolor: theme.home.coralText }} />
            {t('home.hero.eyebrow')}
          </Typography>

          <Typography variant="h1" sx={{
            mt: 2.5, color: theme.home.ink, fontSize: { xs: '2.9rem', sm: '3.6rem', md: '4.2rem' },
            ...reveal(visible, 0.07),
          }}>
            {t('home.hero.titleLine1')}
            <br />{t('home.hero.titleLine2')}{' '}
            <Box component="em" sx={{ fontStyle: 'italic', color: theme.home.cyanText, fontWeight: 500 }}>
              {t('home.hero.titleEm')}
            </Box>
          </Typography>

          <Typography sx={{
            mt: 3, maxWidth: 480, color: theme.home.inkMuted, fontSize: { xs: '1.1rem', md: '1.25rem' }, lineHeight: 1.6,
            ...reveal(visible, 0.14),
          }}>
            {t('home.hero.body')}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4.5, ...reveal(visible, 0.2) }}>
            <Button
              variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(currentUser ? '/dashboard' : '/signup')}
              sx={{
                px: 3.5, py: 1.5, borderRadius: 999, fontSize: '1rem',
                boxShadow: theme.home.cardShadow,
                bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover },
              }}
            >
              {t('home.hero.ctaPrimary')}
            </Button>
            <Button
              variant="text" size="large" onClick={() => navigate('/pricing')}
              sx={{ px: 2, py: 1.5, color: theme.home.ink, fontSize: '1rem', borderRadius: 999,
                '&:hover': { bgcolor: theme.home.paperDeep } }}
            >
              {t('home.hero.ctaSecondary')}
            </Button>
          </Stack>

          <Typography sx={{ mt: 2.5, color: theme.home.inkMuted, fontSize: '0.9rem', ...reveal(visible, 0.26) }}>
            {t('home.hero.noCreditCard')}
          </Typography>
        </Box>

        {/* Right — fanned deck */}
        <Box sx={{ position: 'relative', height: { xs: 320, md: 420 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fan.map((card, i) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks -- fan.length is a fixed constant (4), never changes across renders
            const tiltRef = useCardTilt({ maxAngle: 10, enabled: capability.renderCursor });
            return (
              // Outer wrapper owns positioning ONLY: the `deal` keyframe (a
              // CSS animation with fill-mode `both`) permanently wins the
              // cascade over any imperative `el.style.transform` write for
              // as long as it's applied — so it can never live on the same
              // element useCardTilt writes to, or the tilt hook's transform
              // updates would be silently discarded forever after deal-in
              // finishes. This wrapper plays `deal`; the inner element below
              // only ever receives the tilt hook's inline transform.
              <Box
                key={i}
                sx={{
                  position: 'absolute', width: { xs: 150, md: 200 }, zIndex: card.z,
                  '--tx': `${card.x}px`, '--ty': `${card.y}px`, '--trot': `${card.rot}deg`,
                  animation: visible ? `${deal} 0.65s cubic-bezier(0.16,1,0.3,1) ${0.12 + i * 0.09}s both` : 'none',
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    opacity: visible ? 1 : 0,
                    transform: visible ? `translate(${card.x}px, ${card.y}px) rotate(${card.rot}deg)` : 'none',
                    transition: 'opacity 0.3s ease',
                  },
                  transform: `translate(${card.x}px, ${card.y}px) rotate(${card.rot}deg)`,
                }}
              >
                {/* Inner tilt target: sizing/border/shadow live here, plus
                    the `transition` useCardTilt's inline transform writes
                    animate against — never the `deal` keyframe. */}
                <Box
                  ref={tiltRef}
                  data-cursor="plus"
                  sx={{
                    position: 'relative', width: '100%', borderRadius: 3, overflow: 'hidden',
                    boxShadow: theme.home.cardShadow, border: '4px solid #fff',
                    cursor: 'pointer', transition: 'transform 0.15s ease-out',
                  }}
                >
                  <Box component="img" src={card.src} alt="" sx={{ display: 'block', width: '100%' }} />
                  {/* Sheen must be a sibling overlay, not the <img>'s own
                      background — an <img>'s rendered bitmap always paints
                      over its own CSS background, so a gradient set there
                      would never be visible regardless of the custom
                      properties updating correctly. */}
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: 'absolute', inset: 0, pointerEvents: 'none',
                      background: `radial-gradient(circle at var(--tilt-sheen-x, 50%) var(--tilt-sheen-y, 50%), rgba(255,255,255,0.35), transparent 60%)`,
                      backgroundBlendMode: 'overlay',
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
